import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";
import CustomSnackbar from "../../components/CustomSnackbar";
import KeywordBox from "../../components/KeywordBox";
import TagMigrationService from "./TagMigrationService";
import TagRenameDialog from "./TagRenameDialog";

const PAGE_SIZE = 40;
const SOURCE_STORAGE_KEY = "admin.tag-migration.source";
const LANGUAGE_STORAGE_KEY = "admin.tag-migration.language";
const JIGSAW_LAST_STORAGE_KEY = "admin.tag-migration.jigsawLastPublished";
const KEYWORD_STORAGE_KEY = "admin.tag-migration.keyword";
const PAGE_STORAGE_KEY = "admin.tag-migration.page";
const SORT_STORAGE_KEY = "admin.tag-migration.sort";

const SOURCE_FILTERS = [
    { value: "ALL", label: "All sources" },
    { value: "BEDROCK", label: "Bedrock idea" },
    { value: "WORD_PUZZLE", label: "Word puzzles" },
    { value: "JIGSAW", label: "Jigsaw" },
    { value: "SLIDING", label: "Sliding" },
];

const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

const JIGSAW_LAST_PUBLISHED_FILTERS = [
    { value: "ALL", label: "All" },
    { value: "STANDALONE", label: "Standalone" },
    { value: "NOT_STANDALONE", label: "Not standalone" },
];

function countForSource(row, source) {
    if (source === "BEDROCK") return Number(row.bedrockCount) || 0;
    if (source === "WORD_PUZZLE") return Number(row.wordPuzzleCount) || 0;
    if (source === "JIGSAW") return Number(row.jigsawCount) || 0;
    if (source === "SLIDING") return Number(row.slidingCount) || 0;
    return Number(row.totalCount) || 0;
}

function persistLocal(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {}
}

function loadSourceFilter() {
    try {
        const v = localStorage.getItem(SOURCE_STORAGE_KEY);
        if (SOURCE_FILTERS.some((o) => o.value === v)) return v;
    } catch {}
    return "ALL";
}

function loadLanguageFilter() {
    try {
        const v = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (v === "" || v === "english" || v === "french") return v;
    } catch {}
    return "";
}

function loadJigsawLastFilter() {
    try {
        const v = localStorage.getItem(JIGSAW_LAST_STORAGE_KEY);
        if (JIGSAW_LAST_PUBLISHED_FILTERS.some((o) => o.value === v)) return v;
    } catch {}
    return "ALL";
}

function loadKeywordFilter() {
    try {
        const v = localStorage.getItem(KEYWORD_STORAGE_KEY);
        return typeof v === "string" ? v : "";
    } catch {}
    return "";
}

function loadPage() {
    try {
        const v = parseInt(localStorage.getItem(PAGE_STORAGE_KEY), 10);
        if (Number.isFinite(v) && v >= 1) return v;
    } catch {}
    return 1;
}

const SORT_FIELDS = ["jigsawCount", "slidingCount"];

function loadSort() {
    try {
        const raw = localStorage.getItem(SORT_STORAGE_KEY);
        if (!raw) return { field: null, dir: "desc" };
        const parsed = JSON.parse(raw);
        const field = SORT_FIELDS.includes(parsed?.field) ? parsed.field : null;
        const dir = parsed?.dir === "asc" ? "asc" : "desc";
        return { field, dir };
    } catch {}
    return { field: null, dir: "desc" };
}

function PuzzleCountCell({ total, published }) {
    const t = Number(total) || 0;
    const p = Number(published) || 0;
    return (
        <TableCell align="right">
            {t}
            {t > 0 ? (
                <Typography
                    variant="caption"
                    display="block"
                    color={p > 0 ? "text.secondary" : "warning.main"}
                >
                    {p} live
                </Typography>
            ) : null}
        </TableCell>
    );
}

function LastPublishedStandaloneCell({ value }) {
    if (value === true) {
        return <Chip size="small" color="info" variant="outlined" label="Standalone" />;
    }
    if (value === false) {
        return <Chip size="small" variant="outlined" label="Not standalone" />;
    }
    return (
        <Typography variant="body2" color="text.disabled">—</Typography>
    );
}

export default function TagMigrationMain() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState(loadKeywordFilter);
    const [sourceFilter, setSourceFilter] = useState(loadSourceFilter);
    const [languageFilter, setLanguageFilter] = useState(loadLanguageFilter);
    const [jigsawLastFilter, setJigsawLastFilter] = useState(loadJigsawLastFilter);
    const [page, setPage] = useState(loadPage);
    const [sort, setSort] = useState(loadSort);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [renameRow, setRenameRow] = useState(null);
    const [allTagsOpen, setAllTagsOpen] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        const result = await TagMigrationService.list(languageFilter);
        setLoading(false);
        if (result.success) {
            setRows(result.data);
        } else {
            setRows([]);
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to load tags" });
        }
    }, [languageFilter]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        return rows.filter((row) => {
            if (sourceFilter !== "ALL" && countForSource(row, sourceFilter) <= 0) return false;
            if (jigsawLastFilter === "STANDALONE" && row.jigsawLastStandalone !== true) return false;
            if (jigsawLastFilter === "NOT_STANDALONE" && row.jigsawLastStandalone !== false) return false;
            if (!kw) return true;
            return String(row.tag || "").toLowerCase().includes(kw);
        });
    }, [rows, keyword, sourceFilter, jigsawLastFilter]);

    const sorted = useMemo(() => {
        if (!sort.field) return filtered;
        const dirMul = sort.dir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const d = (Number(a[sort.field]) || 0) - (Number(b[sort.field]) || 0);
            if (d !== 0) return d * dirMul;
            return String(a.tag || "").localeCompare(String(b.tag || ""));
        });
    }, [filtered, sort]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageSafe = Math.min(page, totalPages);
    const pageRows = sorted.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

    const allTagsCsv = useMemo(
        () => filtered
            .map((r) => {
                const tag = String(r.tag || "").trim();
                if (!tag) return "";
                const total = Number(r.totalCount) || 0;
                return `${tag} (${total})`;
            })
            .filter(Boolean)
            .join(", "),
        [filtered],
    );

    const handleSort = (field) => {
        setSort((prev) => {
            const next =
                prev.field === field
                    ? { field, dir: prev.dir === "desc" ? "asc" : "desc" }
                    : { field, dir: "desc" };
            persistLocal(SORT_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
        setPage(1);
        persistLocal(PAGE_STORAGE_KEY, "1");
    };

    const handleOpenRename = (row) => {
        setRenameRow(row);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="h5">Tag migration</Typography>
                <Typography variant="body2" color="text.secondary">
                    Review tags with counts, then rename a tag to regroup it. Renaming into an existing name merges them.
                    Filter by language to match the player app. Jigsaw and Sliding counts include unpublished stories; the public app only lists live published puzzles.
                </Typography>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={languageFilter}
                            label="Language"
                            onChange={(e) => {
                                setLanguageFilter(e.target.value);
                                setPage(1);
                                persistLocal(LANGUAGE_STORAGE_KEY, e.target.value);
                                persistLocal(PAGE_STORAGE_KEY, "1");
                            }}
                        >
                            {LANGUAGE_FILTER_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value || "all"} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                            value={sourceFilter}
                            label="Source"
                            onChange={(e) => {
                                setSourceFilter(e.target.value);
                                setPage(1);
                                persistLocal(SOURCE_STORAGE_KEY, e.target.value);
                                persistLocal(PAGE_STORAGE_KEY, "1");
                            }}
                        >
                            {SOURCE_FILTERS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="jigsaw-last-published-filter-label">Jigsaw last published</InputLabel>
                        <Select
                            labelId="jigsaw-last-published-filter-label"
                            value={jigsawLastFilter}
                            label="Jigsaw last published"
                            onChange={(e) => {
                                setJigsawLastFilter(e.target.value);
                                setPage(1);
                                persistLocal(JIGSAW_LAST_STORAGE_KEY, e.target.value);
                                persistLocal(PAGE_STORAGE_KEY, "1");
                            }}
                        >
                            {JIGSAW_LAST_PUBLISHED_FILTERS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <KeywordBox
                        callback={(kw) => {
                            setKeyword(kw);
                            setPage(1);
                            persistLocal(KEYWORD_STORAGE_KEY, typeof kw === "string" ? kw : "");
                            persistLocal(PAGE_STORAGE_KEY, "1");
                        }}
                        initialValue={keyword}
                        label="Search"
                        placeholder="Search tags"
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 220 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {filtered.length} tag{filtered.length === 1 ? "" : "s"}
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => setAllTagsOpen(true)}>
                        Show all tags
                    </Button>
                </Stack>
            </Stack>

            <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Jigsaw last published</TableCell>
                                <TableCell>Sliding last published</TableCell>
                                <TableCell>Tag</TableCell>
                                <TableCell align="right">Bedrock idea</TableCell>
                                <TableCell align="right">Word puzzles</TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sort.field === "jigsawCount"}
                                        direction={sort.field === "jigsawCount" ? sort.dir : "desc"}
                                        onClick={() => handleSort("jigsawCount")}
                                        sx={{ flexDirection: "row-reverse" }}
                                    >
                                        Jigsaw
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sort.field === "slidingCount"}
                                        direction={sort.field === "slidingCount" ? sort.dir : "desc"}
                                        onClick={() => handleSort("slidingCount")}
                                        sx={{ flexDirection: "row-reverse" }}
                                    >
                                        Sliding
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="center" sx={{ width: 96 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} />
                                    </TableCell>
                                </TableRow>
                            ) : pageRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        No tags found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pageRows.map((row) => (
                                    <TableRow key={row.tag} hover>
                                        <TableCell>
                                            <LastPublishedStandaloneCell value={row.jigsawLastStandalone} />
                                        </TableCell>
                                        <TableCell>
                                            <LastPublishedStandaloneCell value={row.slidingLastStandalone} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined" label={row.tag} />
                                        </TableCell>
                                        <TableCell align="right">{row.bedrockCount || 0}</TableCell>
                                        <TableCell align="right">{row.wordPuzzleCount || 0}</TableCell>
                                        <PuzzleCountCell total={row.jigsawCount} published={row.jigsawPublishedCount} />
                                        <PuzzleCountCell total={row.slidingCount} published={row.slidingPublishedCount} />
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{row.totalCount || 0}</TableCell>
                                        <TableCell align="center">
                                            <Button size="small" onClick={() => handleOpenRename(row)}>
                                                Rename
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                <Pagination
                    count={totalPages}
                    page={pageSafe}
                    onChange={(_, value) => {
                        setPage(value);
                        persistLocal(PAGE_STORAGE_KEY, String(value));
                    }}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Stack>

            <Dialog open={allTagsOpen} onClose={() => setAllTagsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>All tags</DialogTitle>
                <DialogContent>
                    {allTagsCsv ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {allTagsCsv}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">No tags found.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAllTagsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <TagRenameDialog
                open={Boolean(renameRow)}
                onClose={() => setRenameRow(null)}
                initialRow={renameRow}
                initialFromTag={renameRow?.tag || ""}
                language={languageFilter}
                onMessage={(msg) => setSnackbar({ open: true, ...msg })}
                onRenamed={() => fetchList()}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
